import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { makeProduct, label } from './products';
import { createRoom, makeStation } from './room';
import { makeRailSpecimen } from './rails';
import { createMaterialLibrary } from './materials';
import { makeFixedSpecimen } from './fixedWindows';
import { applyMetricUVs, addGlassEdges } from './materialGeometry';
import { scheduleThumbnails } from './thumbnails';
import { makeMT150RailSpecimen } from './mt150RailSpecimen';
import { makeBifoldSpecimen } from './bifold';
import { makeSwingSpecimen } from './swing';
import { makePivotSpecimen } from './pivot';
const categoryOf = p => p.is_fixed ? 'windows' : ['bifold', 'swing', 'pivot'].includes(p.kind) ? p.kind : 'doors';
import { slidingHandleStatus } from './slidingHandle';

export function createEngine(host, products, callbacks, roomConfig) {
  const scene = new THREE.Scene();
  let dirty = true;
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  const gl = renderer.getContext(), debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  const software = !debugInfo || /swiftshader|llvmpipe|software/i.test(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
  renderer.userData = { software };
  renderer.transmissionResolutionScale = software ? .5 : 1;
  renderer.setPixelRatio(software ? .75 : Math.min(window.devicePixelRatio, 1.5));
  renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.shadowMap.autoUpdate = false; renderer.shadowMap.needsUpdate = true;
  renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.04;
  const canvas = renderer.domElement;
  canvas.dataset.testid = 'canvas-3d-showroom'; canvas.setAttribute('aria-label', roomConfig.name); canvas.tabIndex = 0;
  host.appendChild(canvas);
  const camera = new THREE.PerspectiveCamera(43, 1, 0.06, 100);
  const materials = createMaterialLibrary(renderer, () => { dirty = true; }, { roomTheme: 'luxury' });
  canvas.dataset.roomTheme = 'viewlux';
  const room = createRoom(scene, renderer, materials, roomConfig.name, () => { dirty = true; });
  const galleryFog = scene.fog;
  const models = products.map(p => { const model = makeProduct(p, materials); return { ...model, ...makeStation(scene, p, p.room_family_index, model, materials), p }; });
  const primary = models.find(m => m.p.id === 'sd-150');
  const specimens = Object.fromEntries(['sill', 'head'].map(type => {
    const group = makeRailSpecimen(type, primary.frame, materials); group.position.set(0, 0.075, 1.2); group.visible = false; scene.add(group); return [type, group];
  }));
  const mtSpecimens = new Map();
  let activeSpecimen = null, compareIds = [];
  const compareLabels = new THREE.Group(); compareLabels.visible = false; scene.add(compareLabels);
  const specimenName = p => p.family === 'MT150' && p.id !== 'mt150-double' ? `${p.name} ${p.short_label}` : p.name;
  const allSpecimens = () => [...Object.values(specimens), ...mtSpecimens.values()];
  applyMetricUVs(scene); addGlassEdges(scene, materials.glassEdge);
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true; controls.dampingFactor = 0.075;
  controls.minDistance = 1; controls.maxDistance = 20;
  controls.maxPolarAngle = Math.PI * 0.49; controls.enablePan = false;
  let selected = products[0].id, mode = 'orbit', goal = null, running = true, frame = 0;
  let mtVariant = roomConfig.families.find(f => f.id === 'mt150').default_product_id;
  const isActiveFamily = m => m.p.family !== 'MT150' || m.p.id === mtVariant;
  const familyModels = () => models.filter(isActiveFamily);
  const updateStationVisibility = () => models.forEach(m => { m.station.visible = !railDetail && isActiveFamily(m); });
  controls.addEventListener('change', () => { dirty = true; });
  let measurements = false, openings = {}, screenExtensions = {}, dragging = null, yaw = 0, pitch = 0, railDetail = null, installation = 'raised', centerFloor = true, railFloor = true;
  const keys = new Set(), movement = new THREE.Vector3();
  const target = new THREE.Vector3(), raycaster = new THREE.Raycaster(), pointer = new THREE.Vector2();
  const measure = new THREE.Group(); scene.add(measure);
  const stopThumbnails = scheduleThumbnails(products, materials, callbacks.onThumbnails, () => !!goal || !!dragging || !!railDetail, () => selected);
  let previousMobile = host.clientWidth < 700;
  const resize = () => {
    const { width, height } = host.getBoundingClientRect();
    renderer.setSize(width, height); camera.aspect = width / height;
    camera.setViewOffset(width, height, width > 1000 ? 145 : 0, width > 1000 ? (railDetail ? 90 : 18) : -5, width, height);
    camera.updateProjectionMatrix();
    dirty = true;
    const mobile = width < 700;
    if (mobile !== previousMobile && mode === 'orbit') { if (railDetail) inspectRail(railDetail); else focus(selected); }
    if (mode === 'plan') setMode('plan');
    previousMobile = mobile;
  };
  const observer = new ResizeObserver(resize); observer.observe(host); resize();

  function buildMeasurements() {
    while (measure.children.length) {
      const child = measure.children[0]; child.geometry?.dispose(); child.material?.map?.dispose(); child.material?.dispose(); measure.remove(child);
    }
    const m = models.find(m => m.p.id === selected), { width: w, height: h } = m.p;
    measure.position.copy(m.station.position); measure.position.y += m.group.position.y;
    const points = [new THREE.Vector3(-w / 2, h + 0.28, 0.1), new THREE.Vector3(w / 2, h + 0.28, 0.1), new THREE.Vector3(w / 2 + 0.28, 0, 0.1), new THREE.Vector3(w / 2 + 0.28, h, 0.1)];
    for (const x of [-w / 2, w / 2]) points.push(new THREE.Vector3(x, h + 0.20, 0.1), new THREE.Vector3(x, h + 0.36, 0.1));
    for (const y of [0, h]) points.push(new THREE.Vector3(w / 2 + 0.20, y, 0.1), new THREE.Vector3(w / 2 + 0.36, y, 0.1));
    measure.add(new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(points), new THREE.LineBasicMaterial({ color: '#eadbc2' })));
    const wl = label(`${Math.round(w * 1000).toLocaleString()} mm`, 0.85, 0.22, { color: '#fff4e1', size: 115 }); wl.position.set(0, h + 0.43, 0.1); measure.add(wl);
    const hl = label(`${Math.round(h * 1000).toLocaleString()} mm`, 0.85, 0.22, { color: '#fff4e1', size: 115 }); hl.position.set(w / 2 + 0.6, h / 2, 0.1); hl.rotation.z = Math.PI / 2; measure.add(hl);
    measure.visible = measurements;
  }

  function focus(id, instant = false) {
    dirty = true; renderer.shadowMap.needsUpdate = true;
    room.setInspection(false);
    if (railDetail) { railDetail = null; activeSpecimen = null; allSpecimens().forEach(g => { g.visible = false; }); compareLabels.visible = false; controls.minDistance = 1; controls.maxDistance = 20; callbacks.onRailDetail(null); }
    selected = id; const m = models.find(m => m.p.id === id);
    const width = host.clientWidth, height = host.clientHeight;
    camera.setViewOffset(width, height, width > 1000 ? 145 : 0, width > 1000 ? 18 : -5, width, height);
    models.forEach(model => {
      model.outline.visible = model.p.id === id;
    });
    updateStationVisibility();
    target.copy(m.station.position).add(new THREE.Vector3(m.viewCenterX || 0, m.raised + (m.viewHeightOffset || 0) + m.p.height * 0.49, 0));
    const mobile = host.clientWidth < 700;
    const d = Math.max(m.viewWidth || m.p.width, m.p.height) * (mobile ? 2.65 : 1.85);
    // Face each family toward the central back-wall branding. Opposite camera
    // angles keep the same physical wall sign in view for both display stations.
    const distanceZ = d * 1.15;
    const pos = target.clone().add(new THREE.Vector3(target.x * distanceZ / (8.79 + target.z), d * 0.3, distanceZ));
    pos.x = THREE.MathUtils.clamp(pos.x, -11.2, 11.2);
    if (instant) { camera.position.copy(pos); controls.target.copy(target); controls.update(); }
    else goal = { pos, target: target.clone() };
    buildMeasurements();
  }
  function setMode(value) {
    dirty = true;
    if (railDetail && value === 'orbit') { mode = 'orbit'; canvas.dataset.mode = mode; controls.enabled = true; return; }
    if (railDetail && value !== 'orbit') { focus(selected); }
    mode = value; keys.clear(); controls.enabled = mode === 'orbit';
    updateStationVisibility();
    scene.fog = mode === 'plan' ? null : galleryFog;
    if (mode === 'plan') {
      const active = familyModels(), minX = Math.min(...active.map(m => m.p.position[0] - (m.viewWidth || m.p.width) / 2)), maxX = Math.max(...active.map(m => m.p.position[0] + (m.viewWidth || m.p.width) / 2));
      const cx = (minX + maxX) / 2, distance = Math.max(12, (maxX - minX + 3) / 2 / Math.max(camera.aspect, .35) / Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)));
      goal = { pos: new THREE.Vector3(cx, distance, distance * .15), target: new THREE.Vector3(cx, 0, 0) };
    } else if (mode === 'orbit') focus(selected);
    else {
      goal = null; camera.position.y = 1.65;
      const direction = new THREE.Vector3(); camera.getWorldDirection(direction);
      yaw = Math.atan2(-direction.x, -direction.z); pitch = 0;
      camera.rotation.order = 'YXZ'; camera.rotation.set(pitch, yaw, 0);
    }
    canvas.dataset.mode = mode;
  }
  function specimenFor(model, type) {
    if (model.p.id === 'sd-150') return specimens[type];
    const key = `${model.p.id}:${type}`;
    if (!mtSpecimens.has(key)) {
      const specimen = model.p.is_fixed ? makeFixedSpecimen(model.p, type, model.frame, materials) : model.p.kind === 'bifold' ? makeBifoldSpecimen(model.p, type, model.frame, materials) : model.p.kind === 'swing' ? makeSwingSpecimen(model.p, type, model.frame, materials) : model.p.kind === 'pivot' ? makePivotSpecimen(model.p, type, model.frame, materials) : makeMT150RailSpecimen(model.p, type, model.frame, materials);
      scene.add(specimen); applyMetricUVs(specimen); addGlassEdges(specimen, materials.glassEdge);
      mtSpecimens.set(key, specimen);
    }
    return mtSpecimens.get(key);
  }
  function clearCompareLabels() {
    while (compareLabels.children.length) { const c = compareLabels.children[0]; c.geometry.dispose(); c.material.map?.dispose(); c.material.dispose(); compareLabels.remove(c); }
  }
  function inspectRail(type) {
    dirty = true; renderer.shadowMap.needsUpdate = true;
    if (!type) { focus(selected); return; }
    const model = models.find(m => m.p.id === selected);
    if (!['sill', 'head'].includes(type) || (model.p.id !== 'sd-150' && model.p.family !== 'MT150' && !model.p.is_fixed && !['bifold', 'swing', 'pivot'].includes(model.p.kind))) return;
    const shown = [model, ...compareIds.map(id => models.find(m => m.p.id === id)).filter(m => m && m !== model && categoryOf(m.p) === categoryOf(model.p))];
    const groups = shown.map(m => specimenFor(m, type));
    activeSpecimen = groups[0];
    room.setInspection(true);
    railDetail = type; mode = 'orbit'; canvas.dataset.mode = mode; controls.enabled = true; keys.clear();
    canvas.dataset.railCompare = shown.slice(1).map(m => m.p.id).join(',') || 'none';
    const width = host.clientWidth, height = host.clientHeight;
    camera.setViewOffset(width, height, width > 1000 ? 145 : 0, width > 1000 ? 90 : -5, width, height);
    callbacks.onMode('orbit');
    models.forEach(m => { m.station.visible = false; });
    allSpecimens().forEach(group => { group.visible = groups.includes(group); });
    groups.forEach(g => g.position.set(0, .075, 1.2));
    const sizes = groups.map(g => new THREE.Box3().setFromObject(g)), gap = .22;
    const total = sizes.reduce((sum, b) => sum + b.max.x - b.min.x, 0) + gap * (groups.length - 1);
    let cursor = -total / 2;
    groups.forEach((g, i) => { const b = sizes[i]; g.position.x = cursor - b.min.x; cursor += b.max.x - b.min.x + gap; });
    clearCompareLabels();
    if (groups.length > 1) groups.forEach((g, i) => {
      const b = new THREE.Box3().setFromObject(g), tag = label(specimenName(shown[i].p), .5, .125, { color: '#16332d', size: 120, font: '700' });
      tag.position.set((b.min.x + b.max.x) / 2, b.max.y + .1, b.max.z + .02); compareLabels.add(tag);
    });
    compareLabels.visible = groups.length > 1;
    measure.visible = false;
    const union = groups.reduce((box, g) => box.union(new THREE.Box3().setFromObject(g)), new THREE.Box3());
    const scale = Math.max(1, (union.max.x - union.min.x) / .55);
    controls.minDistance = 0.12; controls.maxDistance = 2 * scale;
    const centre = groups.length > 1 || model.p.family === 'MT150' || model.p.is_fixed || ['bifold', 'swing', 'pivot'].includes(model.p.kind)
      ? union.getCenter(new THREE.Vector3())
      : new THREE.Vector3(0, type === 'sill' ? 0.095 : 0.12, 1.2);
    const multiplier = (host.clientWidth < 700 ? 2.05 : 1.7) * (groups.length > 1 ? scale * (host.clientWidth < 700 ? 1.7 : .85) : 1);
    goal = { pos: centre.clone().add(new THREE.Vector3(groups.length > 1 ? 0.12 : 0.43, 0.27, 0.36).multiplyScalar(multiplier)), target: centre };
  }
  const keyDown = event => {
    if (event.target.closest('input,textarea,button,[role="dialog"]') || document.querySelector('[role="dialog"]')) return;
    if (mode === 'walk' && ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) { event.preventDefault(); keys.add(event.code); }
  };
  const keyUp = event => keys.delete(event.code);
  const clear = () => keys.clear();
  window.addEventListener('keydown', keyDown); window.addEventListener('keyup', keyUp); window.addEventListener('blur', clear);
  const down = e => { goal = null; dragging = { x: e.clientX, y: e.clientY, startX: e.clientX, startY: e.clientY }; if (mode === 'walk') canvas.setPointerCapture(e.pointerId); };
  const move = e => {
    if (mode === 'walk' && dragging) {
      yaw -= (e.clientX - dragging.x) * 0.005; pitch = THREE.MathUtils.clamp(pitch - (e.clientY - dragging.y) * 0.004, -1.15, 1.15);
      camera.rotation.set(pitch, yaw, 0); dragging.x = e.clientX; dragging.y = e.clientY;
      dirty = true;
    }
  };
  const up = e => {
    if (!railDetail && dragging && Math.hypot(e.clientX - dragging.startX, e.clientY - dragging.startY) < 6) {
      const r = canvas.getBoundingClientRect(); pointer.set((e.clientX - r.left) / r.width * 2 - 1, -(e.clientY - r.top) / r.height * 2 + 1);
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(familyModels().filter(m => m.station.visible).map(m => m.station), true);
      const hit=hits.find(item=>item.object.userData.productId);
      canvas.dataset.lastPick=hit?.object.userData.productId || 'none';
      if (hit) callbacks.onSelect(hit.object.userData.productId);
    }
    dragging = null;
  };
  canvas.addEventListener('pointerdown', down); canvas.addEventListener('pointermove', move); canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);
  focus(selected, true); setMode('orbit'); goal = null;
  let lastFrame = performance.now(), lastRender = 0, readinessReported = false;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function renderCurrentFrame() {
    canvas.dataset.camera = camera.position.toArray().map(v => v.toFixed(3)).join(',');
    canvas.dataset.cameraSettled=goal ? 'false' : 'true';
    canvas.dataset.opening = models.find(m => m.p.id === selected).progress.toFixed(3);
    canvas.dataset.selected = selected;
    canvas.dataset.room = roomConfig.id;
    canvas.dataset.familyCount = String(roomConfig.families.length);
    canvas.dataset.mt150Variant = mtVariant;
    canvas.dataset.visibleProducts = JSON.stringify(models.filter(m => m.station.visible).map(m => m.p.id));
    canvas.dataset.railDetail = railDetail || 'none';
    canvas.dataset.railProduct = railDetail ? selected : 'none';
    canvas.dataset.railProfileCode = activeSpecimen?.userData.profileCode || '';
    canvas.dataset.railTrackCount = String(activeSpecimen?.userData.trackCount || 0);
    canvas.dataset.railProfileDepth = activeSpecimen?.userData.profileDepth?.toFixed(3) || '0';
    canvas.dataset.visibleRailSpecimens = String(allSpecimens().filter(g => g.visible).length);
    canvas.dataset.installation = installation;
    canvas.dataset.sillOffset = installation === 'flush' ? '-50' : '0';
    canvas.dataset.fullRailFill = centerFloor && railFloor ? 'on' : 'off';
    canvas.dataset.centerFloor = primary.centerInfill.visible ? 'visible' : 'hidden';
    canvas.dataset.railFloor = primary.railFloorInfill.visible ? 'visible' : 'hidden';
    canvas.dataset.railFloorStrips = String(primary.railFloorInfill.visible ? primary.railFloorInfill.children.length : 0);
    canvas.dataset.textureState = materials.status;
    canvas.dataset.textureCount = String(materials.textureCount);
    canvas.dataset.materialVersion = '3';
    canvas.dataset.glassMode = software ? 'reflective-transparent' : 'physical-transmission';
    const current = models.find(m => m.p.id === selected);
    canvas.dataset.screenExtension = (current.screenProgress || 0).toFixed(3);
    canvas.dataset.panelCount = String(current.panelCount || current.p.panel_count || 0);
    canvas.dataset.trackCount = String(current.trackCount || current.p.track_count || 0);
    canvas.dataset.panelPositions = JSON.stringify(current.group.userData.panelPositions || []);
    canvas.dataset.handlePositions = slidingHandleStatus(current.group);
    canvas.dataset.frameAlbedo = `#${current.frame.color.getHexString()}`;
    canvas.dataset.productOperation = current.p.is_fixed ? 'fixed' : ['bifold', 'swing', 'pivot'].includes(current.p.kind) ? current.p.kind : 'sliding';
    canvas.dataset.fixedProfileDepth = current.p.is_fixed ? String(current.group.userData.sourceProfileDepth) : '';
    renderer.render(scene, camera);
    // Actual projected mesh/plinth points for deterministic pointer regression.
    // Selection still runs through normal pointer events and Three.js raycasts.
    canvas.dataset.pickTargets=JSON.stringify(familyModels().filter(m=>m.station.visible).flatMap(m=>[
      ['glass',new THREE.Vector3(0,m.p.height/2,0).applyMatrix4(m.group.matrixWorld)],
      ['plinth',new THREE.Vector3(m.viewCenterX||0,.061,.48).applyMatrix4(m.station.matrixWorld)],
    ].map(([surface,point])=>{point.project(camera);return {id:m.p.id,surface,x:(point.x+1)*canvas.clientWidth/2,y:(1-point.y)*canvas.clientHeight/2,inView:Math.abs(point.x)<1&&Math.abs(point.y)<1&&Math.abs(point.z)<1};})));
    canvas.dataset.drawCalls = String(renderer.info.render.calls);
    canvas.dataset.triangles = String(renderer.info.render.triangles);
    lastRender = performance.now(); dirty = false;
    // Readiness means the textured, saved-state scene has actually rendered,
    // not merely that the canvas was attached while image requests were pending.
    if (!readinessReported && materials.status === 'error') {
      readinessReported = true;
      callbacks.onError('Some showroom materials could not load. Please try again.');
    } else if (!readinessReported && materials.status === 'ready' && canvas.dataset.wallLogoState !== 'loading') {
      readinessReported = true;
      callbacks.onReady();
    }
  }
  function tick() {
    if (!running) return;
    frame = requestAnimationFrame(tick); const now = performance.now(); const dt = Math.min((now - lastFrame) / 1000, 0.1); lastFrame = now;
    if (goal) {
      dirty = true;
      if (!goal.from) { goal.from = camera.position.clone(); goal.fromTarget = controls.target.clone(); goal.start = now; }
      // Wall-clock transitions finish even when a software frame is slow.
      const progress = reduced ? 1 : Math.min((now - goal.start) / 750, 1);
      const eased = progress * progress * (3 - 2 * progress);
      camera.position.lerpVectors(goal.from, goal.pos, eased); controls.target.lerpVectors(goal.fromTarget, goal.target, eased);
      camera.lookAt(controls.target);
      if (progress === 1) goal = null;
    }
    if (mode === 'orbit') controls.update();
    if (mode === 'walk') {
      const forward = Number(keys.has('KeyW') || keys.has('ArrowUp')) - Number(keys.has('KeyS') || keys.has('ArrowDown'));
      const side = Number(keys.has('KeyD') || keys.has('ArrowRight')) - Number(keys.has('KeyA') || keys.has('ArrowLeft'));
      movement.set(side, 0, -forward); if (movement.length()) movement.normalize().applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw).multiplyScalar(dt * 2.8);
      const next = camera.position.clone().add(movement);
      // Keep visitors on the gallery floor and outside the display plinths.
      const blocked = familyModels().some(m => Math.abs(next.x - (m.p.position[0] + (m.footprint?.centerX || 0))) < Math.max((m.footprint?.width || m.p.width) + 0.65, 2.6) / 2 + 0.16 && Math.abs(next.z - m.p.position[2]) < 0.82);
      if (!blocked && movement.lengthSq() > 0) { camera.position.x = THREE.MathUtils.clamp(next.x, -10.8, 10.8); camera.position.z = THREE.MathUtils.clamp(next.z, -8, 24); dirty = true; }
    }
    models.forEach(m => {
      const to = (openings[m.p.id] || 0) / 100;
      if (m.progress !== to) {
        if (!m.transition || m.transition.target !== to) m.transition = { from: m.progress, target: to, start: now };
        const t = reduced ? 1 : Math.min((now - m.transition.start) / 1100, 1);
        const eased = t * t * (3 - 2 * t);
        m.progress = t === 1 ? to : m.transition.from + (to - m.transition.from) * eased;
        if (t === 1) m.transition = null;
        m.animate(m.progress); dirty = true; renderer.shadowMap.needsUpdate = true;
      }
      if (m.animateScreen) {
        const extension = (screenExtensions[m.p.id] || 0) / 100;
        if (m.screenProgress !== extension) {
          if (!m.screenTransition || m.screenTransition.target !== extension) m.screenTransition = { from: m.screenProgress, target: extension, start: now };
          const t = reduced ? 1 : Math.min((now - m.screenTransition.start) / 1100, 1), eased = t * t * (3 - 2 * t);
          m.screenProgress = t === 1 ? extension : m.screenTransition.from + (extension - m.screenTransition.from) * eased;
          if (t === 1) m.screenTransition = null;
          m.animateScreen(m.screenProgress); dirty = true; renderer.shadowMap.needsUpdate = true;
        }
      }
    });
    if (dirty && (!software || now - lastRender >= 90)) renderCurrentFrame();
  }
  tick();
  return {
    setMTVariant: id => { if (models.some(m => m.p.id === id && m.p.family === 'MT150')) { mtVariant = id; updateStationVisibility(); dirty = true; renderer.shadowMap.needsUpdate = true; } },
    select: id => { focus(id); if (mode !== 'orbit') { callbacks.onMode('orbit'); setMode('orbit'); } },
    setMode, reset: () => { setMode('orbit'); callbacks.onMode('orbit'); focus(selected); },
    inspectRail,
    setRailCompare: ids => { compareIds = ids || []; if (railDetail) inspectRail(railDetail); },
    renderNow: renderCurrentFrame,
    setFullRailFill: enabled => {
      centerFloor = railFloor = enabled;
      specimens.sill.userData.setCenterFloor(enabled);
      specimens.sill.userData.setRailFloor(enabled);
      primary.centerInfill.visible = primary.railFloorInfill.visible = installation === 'flush' && enabled;
      dirty = true; renderer.shadowMap.needsUpdate = true;
    },
    setInstallation: value => {
      installation = value;
      primary.group.position.y = 0.06 - (value === 'flush' ? 0.05 : 0);
      specimens.sill.userData.setInstallation(value);
      primary.centerInfill.visible = value === 'flush' && centerFloor;
      primary.railFloorInfill.visible = value === 'flush' && railFloor;
      buildMeasurements(); if (railDetail) measure.visible = false;
      dirty = true; renderer.shadowMap.needsUpdate = true;
    },
    setCenterFloor: enabled => {
      centerFloor = enabled;
      specimens.sill.userData.setCenterFloor(enabled);
      primary.centerInfill.visible = installation === 'flush' && enabled;
      dirty = true; renderer.shadowMap.needsUpdate = true;
    },
    setOpenings: value => { openings = value; },
    setScreenExtensions: value => { screenExtensions = value; },
    setRailFloor: enabled => {
      railFloor = enabled;
      specimens.sill.userData.setRailFloor(enabled);
      primary.railFloorInfill.visible = installation === 'flush' && enabled;
      dirty = true; renderer.shadowMap.needsUpdate = true;
    },
    setFinish: finish => {
      if (!finish) return;
      models.forEach(m => materials.applyFinish(m.frame, finish));
      canvas.dataset.finish = finish.id;
      dirty = true;
    },
    setLighting: value => { room.setLighting(value); dirty = true; renderer.shadowMap.needsUpdate = true; },
    setMeasurements: value => { measurements = value; measure.visible = value && !railDetail; dirty = true; },
    move: (direction, pressed) => { const key = { up: 'KeyW', down: 'KeyS', left: 'KeyA', right: 'KeyD' }[direction]; if (pressed) keys.add(key); else keys.delete(key); },
    dispose: () => {
      running = false; cancelAnimationFrame(frame); stopThumbnails(); observer.disconnect(); controls.dispose(); room.dispose();
      window.removeEventListener('keydown', keyDown); window.removeEventListener('keyup', keyUp); window.removeEventListener('blur', clear);
      const geometries = new Set(), otherMaterials = new Set(), otherTextures = new Set();
      scene.traverse(o => {
        if (o.geometry) geometries.add(o.geometry);
        (o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []).forEach(m => {
          if (m.userData.managed) return;
          otherMaterials.add(m);
          Object.values(m).forEach(value => { if (value?.isTexture) otherTextures.add(value); });
        });
      });
      geometries.forEach(g => g.dispose()); otherMaterials.forEach(m => m.dispose()); otherTextures.forEach(t => t.dispose()); materials.dispose();
      renderer.dispose(); renderer.forceContextLoss(); canvas.remove();
    },
  };
}